import { format } from "date-fns";
import { isProxy, isReactive, isRef, toRaw } from "vue";

/**
 *
 * @param name Cookie name
 * @returns Cookie value
 */
export function getCookie(name: string) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === name + "=") {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

export const humanFormatNumber = (
  value: string | number,
  fractionDigits = 2,
) => {
  let number = Number(value);
  if (isNaN(number)) {
    return value;
  } else {
    let number = Number(value);
    let magnitude = 0;
    while (Math.abs(number) >= 1000) {
      magnitude++;
      number /= 1000.0;
    }
    // avoid showing decimal points for whole numbers
    if (magnitude === 0) {
      return number;
    } else {
      return `${number.toFixed(fractionDigits)}${
        ["", "K", "M", "B", "T"][magnitude]
      }`;
    }
  }
};

export const humanFormatMinutes = (minutes: number | string) => {
  let number = Number(minutes);
  if (!isNaN(number)) {
    if (number < 60) {
      return {
        value: minutes,
        unit: number > 1 ? "minutes" : "minute",
      };
    } else {
      const hours = number / 60;
      return {
        value: hours.toFixed(1),
        unit: hours >= 2 ? "hours" : "hour",
      };
    }
  }
  return {
    value: "",
    unit: "",
  };
};

export const timeLongAgo = (dateString: string): string => {
  // For recent dates (< 24h), show relative time
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const oneDay = 24 * 60 * 60 * 1000;

  if (diff < oneDay) {
    const hours = Math.floor(diff / (60 * 60 * 1000));
    if (hours === 0) {
      const minutes = Math.floor(diff / (60 * 1000));
      if (minutes === 0) {
        return "just now";
      }
      return `${minutes}m ago`;
    }
    return `${hours}h ago`;
  }

  // For older dates, show the actual date
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const humanFormatDataSize = (
  bytes: number | string,
  fractionDigits = 2,
) => {
  let number = Number(bytes);
  if (isNaN(number)) {
    return {
      value: bytes,
      unit: "",
    };
  } else {
    let number = Number(bytes);
    let magnitude = 0;
    while (Math.abs(number) >= 1024) {
      magnitude++;
      number /= 1024.0;
    }
    return {
      value: number.toFixed(fractionDigits),
      unit: ["bytes", "KB", "MB", "GB", "TB"][magnitude],
    };
  }
};

export function getUTCTimestamp(date: string): string {
  const d = new Date(date);
  const utcTime = d.getTime() + d.getTimezoneOffset() * 60000;
  return new Date(utcTime).toISOString();
}

export function downloadFileFromURL(url: string, filename: string) {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    })
    .catch((error) => {
      console.error("Error downloading file", error);
      alert("Error downloading file");
    });
}

/**
 * Convert the reactive object to raw object by removing the deep reactivity
 * Source: https://github.com/vuejs/core/issues/5303#issuecomment-1543596383
 * @param sourceObj The reactive object
 * @returns The raw object
 */

export function deepToRaw<T extends Record<string, any>>(sourceObj: T): T {
  const objectIterator = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map((item) => objectIterator(item));
    }
    if (isRef(input) || isReactive(input) || isProxy(input)) {
      return objectIterator(toRaw(input));
    }
    if (input && typeof input === "object") {
      return Object.keys(input).reduce((acc, key) => {
        acc[key as keyof typeof acc] = objectIterator(input[key]);
        return acc;
      }, {} as T);
    }
    return input;
  };

  return objectIterator(sourceObj);
}

export async function copyToClipboard(
  text: string,
  message: string = "Copied to clipboard",
  target?: HTMLElement,
  showAlert: boolean = true,
) {
  await navigator.clipboard.writeText(text);
  // add small tooltip text next to the target by making the target as relative
  // and adding a span element with the tooltip text
  // TODO: not working properly. The event target is svg path and the message
  // cannot be attached as child to this.
  if (!showAlert) return;
  if (target) {
    const tooltip = document.createElement("span");
    tooltip.textContent = message;
    tooltip.className =
      "text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-md absolute top-0 left-full ml-2";
    target.appendChild(tooltip);

    setTimeout(() => {
      tooltip.remove();
    }, 100000);
  } else {
    window.alert(message);
  }
}

export function downloadFromUrl(url: string, filename: string) {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    })
    .catch((error) => {
      console.error("Error downloading file", error);
      alert("Error downloading file");
    });
}

export function formatDate(date: string) {
  return format(new Date(date), "MMM dd yyyy");
}

export function formatTimestamp(timestamp: string) {
  return format(new Date(timestamp), "MMM dd yyyy, hh:mm a");
}

// Deep clone function for nested objects
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepClone(item)) as unknown as T;
  }

  const cloned = {} as T;
  Object.entries(obj).forEach(([key, value]) => {
    (cloned as any)[key] = deepClone(value);
  });

  return cloned;
}

export const dateLongAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
  }

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
